import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

interface FrameworkCardProps {
  title: string;
  description: string;
  features: string[];
  imageSrc: string;
  videoSrc: string;
  linkTo: string;
  variant?: 'default' | 'special';
}

const FrameworkCard: React.FC<FrameworkCardProps> = ({
  title,
  description,
  features,
  imageSrc,
  videoSrc,
  linkTo,
  variant = 'default'
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  
  const handleClick = () => {
    // Only navigate if linkTo is provided
    if (linkTo) {
      // Use window.location to perform a full page reload
      window.location.href = linkTo;
    }
  };

  // Define framework icon based on title
  const getFrameworkIcon = () => {
    switch(title.toLowerCase()) {
      case 'aifmd':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
          </svg>
        );
      case 'csrd':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'nis2':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
          </svg>
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`relative overflow-hidden transition-all duration-300 ${linkTo ? 'cursor-pointer' : 'cursor-default'} h-full ${
        isHovered && linkTo ? 'transform scale-[1.02]' : ''
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {/* Apple-style glass card with subtle shadow */}
      <div className="relative backdrop-blur-md rounded-2xl overflow-hidden h-full flex flex-col">
        {/* Card background with Apple-style glass morphism */}
        <div className={`absolute inset-0 ${variant === 'special' ? 'bg-emerald-700/90' : 'bg-white/60'} backdrop-blur-lg`}></div>
        
        {/* Card border with subtle highlight */}
        <div className={`absolute inset-0 rounded-2xl border ${variant === 'special' ? 'border-emerald-600/50' : 'border-white/50'} shadow-[0_0_20px_rgba(0,0,0,0.03)]`}></div>
        
        {/* Inner highlight effect */}
        <div className={`absolute top-0 h-1/2 w-full bg-gradient-to-b ${variant === 'special' ? 'from-emerald-600/40 to-transparent' : 'from-white/40 to-transparent'}`}></div>
        
        <div className="relative flex flex-col h-full">
          {/* Header */}
          <div className="p-6 flex items-center space-x-4">
            {/* Framework icon */}
            <div className={`w-12 h-12 rounded-full ${variant === 'special' ? 'bg-white' : 'bg-emerald-100'} flex items-center justify-center flex-shrink-0`}>
              {getFrameworkIcon()}
            </div>
            
            <div>
              <h3 className={`text-xl font-semibold ${variant === 'special' ? 'text-white' : ''}`}>{title}</h3>
              <div className={`h-0.5 w-16 ${variant === 'special' ? 'bg-white' : 'bg-emerald-700'} mt-1 rounded-full transition-all duration-300 ${
                isHovered ? 'w-24' : 'w-16'
              }`}></div>
            </div>
          </div>
          
          {/* Description */}
          <div className="px-6 pb-4">
            <p className={`text-sm ${variant === 'special' ? 'text-white/90' : 'text-gray-600'}`}>{description}</p>
          </div>
          
          {/* Features */}
          <div className="px-6 space-y-2 flex-grow">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start">
                <svg className={`h-4 w-4 ${variant === 'special' ? 'text-white' : 'text-emerald-700'} mr-2 mt-0.5 flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className={`text-xs ${variant === 'special' ? 'text-white/90' : 'text-gray-700'}`}>{feature}</span>
              </div>
            ))}
          </div>
          
          {/* Call to action section - always rendered for consistent spacing */}
          <div className="px-6 py-6 mt-auto">
            {/* Show "Learn More" only when linkTo is provided, otherwise show empty space */}
            {linkTo ? (
              <div className={`flex items-center ${variant === 'special' ? 'text-white' : 'text-emerald-700'} text-sm font-medium transition-all duration-300 ${
                isHovered ? 'translate-x-1' : ''
              }`}>
                <span>{t('homepage.learnMore')}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform duration-300 ${
                    isHovered ? 'translate-x-1' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </div>
            ) : (
              <div className="h-4" aria-hidden="true"></div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FrameworkCard;